import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { format, parseISO } from 'date-fns';
import FormLinkGenerator from '../components/FormLinkGenerator';

function HaloIntegrationPage() {
  const { integrationKey } = useParams();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkFormData, setLinkFormData] = useState({
    agentId: '',
    lockAgent: false,
    meetingType: 'phone',
    duration: 30,
  });

  const [activeTab, setActiveTab] = useState('scheduling');
  const [formsData, setFormsData] = useState(null);
  const [formsLoading, setFormsLoading] = useState(false);

  // Modal state for reschedule/cancel
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'reschedule' or 'cancel'
    booking: null,
    reason: '',
    processing: false,
    error: null,
  });

  useEffect(() => {
    if (ticketId) {
      loadData();
      loadAgents();
    } else {
      setError('No ticket ID provided');
      setLoading(false);
    }
  }, [ticketId]);

  const loadFormsData = async () => {
    try {
      setFormsLoading(true);
      const response = await api.getTicketForms(ticketId, integrationKey);
      setFormsData(response);
    } catch (err) {
      console.error('Error loading forms data:', err);
    } finally {
      setFormsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'forms' && ticketId && !formsData) {
      loadFormsData();
    }
  }, [activeTab, ticketId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.getTicketLinks(ticketId, integrationKey);
      setData(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to load ticket data');
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const agentsData = await api.getAgents();
      setAgents(agentsData);
    } catch (err) {
      console.error('Error loading agents:', err);
    }
  };

  const handleGenerateLink = async () => {
    try {
      setGeneratingLink(true);
      await api.generateLink({
        ticketId,
        agentId: linkFormData.agentId || null,
        lockedAgentId: (linkFormData.lockAgent && linkFormData.agentId) ? linkFormData.agentId : null,
        meetingType: linkFormData.meetingType,
        duration: parseInt(linkFormData.duration),
      }, integrationKey);

      // Reload data to show new link
      await loadData();
      setShowLinkForm(false);
      setLinkFormData({ agentId: '', lockAgent: false, meetingType: 'phone', duration: 30 });
      setGeneratingLink(false);
    } catch (err) {
      alert('Failed to generate link');
      setGeneratingLink(false);
    }
  };

  const copyLink = async (url) => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    } catch (err) {
      // Fallback for iframe/permission issues
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Link copied to clipboard!');
      } catch (execErr) {
        // If all else fails, show the URL
        alert(`Copy this link:\n\n${url}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const getStatusBadge = (booking) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-primary-100 text-primary-800',
    };
    return colors[booking.status] || 'bg-gray-100 text-gray-800';
  };

  const openModal = (type, booking) => {
    setModalState({
      isOpen: true,
      type,
      booking,
      reason: '',
      processing: false,
      error: null,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      booking: null,
      reason: '',
      processing: false,
      error: null,
    });
  };

  const handleModalSubmit = async () => {
    const { type, booking, reason } = modalState;

    // Validate reason for cancel
    if (type === 'cancel' && (!reason || reason.trim() === '')) {
      setModalState(prev => ({ ...prev, error: 'Reason is required for cancellation' }));
      return;
    }

    setModalState(prev => ({ ...prev, processing: true, error: null }));

    try {
      if (type === 'reschedule') {
        await api.techRescheduleBooking(booking.bookingId, reason || null, integrationKey);
      } else {
        await api.techCancelBooking(booking.bookingId, reason, integrationKey);
      }

      // Reload data and close modal
      await loadData();
      closeModal();
    } catch (err) {
      console.error(`Error ${type}ing booking:`, err);
      setModalState(prev => ({
        ...prev,
        processing: false,
        error: err.response?.data?.error || `Failed to ${type} booking`,
      }));
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl">
      <div className="mb-4">
        <p className="text-xs text-gray-600 mb-2">Ticket #{ticketId}</p>
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('scheduling')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'scheduling'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Scheduling
          </button>
          <button
            onClick={() => setActiveTab('forms')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'forms'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Forms
          </button>
        </div>
      </div>

      {/* Forms Tab */}
      {activeTab === 'forms' && (
        <div>
          <FormLinkGenerator
            ticketId={ticketId}
            integrationKey={integrationKey}
            onLinkGenerated={() => loadFormsData()}
          />

          {formsLoading && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          )}

          {formsData && !formsLoading && (
            <div className="mt-4">
              {/* Form Links */}
              {formsData.links && formsData.links.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Form Links</h3>
                  <div className="space-y-2">
                    {formsData.links.map((link) => (
                      <div key={link.formToken} className="bg-white border border-gray-200 rounded p-3 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{link.formTitle}</span>
                          {link.isUsed ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Submitted</span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Pending</span>
                          )}
                        </div>
                        {link.clientName && (
                          <div className="text-gray-600 mb-1">Client: {link.clientName}</div>
                        )}
                        <div className="bg-gray-50 rounded px-2 py-1 mb-2 break-all font-mono text-xs text-gray-700">
                          {`${window.location.origin}/f/${link.formToken}`}
                        </div>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/f/${link.formToken}`;
                            navigator.clipboard.writeText(url).then(
                              () => alert('Link copied!'),
                              () => alert(`Copy this link:\n\n${url}`)
                            );
                          }}
                          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Copy Link
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submissions */}
              {formsData.submissions && formsData.submissions.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Submissions</h3>
                  <div className="space-y-2">
                    {formsData.submissions.map((sub) => (
                      <div key={sub.submissionId} className="bg-white border border-gray-200 rounded p-3 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">{sub.clientName || 'Unknown'}</span>
                          <span className="text-gray-500">{new Date(sub.createdAt).toLocaleDateString()}</span>
                        </div>
                        {sub.haloTicketId && (
                          <div className="text-gray-600">Halo Ticket: #{sub.haloTicketId}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formsData.links.length === 0 && formsData.submissions.length === 0 && (
                <div className="text-center py-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded">
                  No forms sent for this ticket yet
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scheduling Tab */}
      {activeTab === 'scheduling' && <>
      {/* Existing Bookings */}
      {data.bookings && data.bookings.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Scheduled Appointments</h3>
          <div className="space-y-2">
            {data.bookings.map((booking) => (
              <div
                key={booking.bookingId}
                className="bg-white border border-gray-200 rounded p-3 text-sm"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{booking.customerName}</div>
                    <div className="text-gray-600 text-xs">{booking.customerEmail}</div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(booking)}`}>
                    {booking.status}
                  </span>
                </div>
                <div className="text-xs text-gray-700 mb-2">
                  <div><strong>Date:</strong> {format(parseISO(booking.startTime), 'MMM d, yyyy')}</div>
                  <div><strong>Time:</strong> {format(parseISO(booking.startTime), 'h:mm a')} ({booking.duration} min)</div>
                  <div><strong>Type:</strong> {booking.meetingDetails?.joinUrl ? 'Teams Meeting' : 'Phone Call'}</div>
                </div>

                {/* Action buttons for confirmed bookings */}
                {booking.status === 'confirmed' && (
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => openModal('reschedule', booking)}
                      className="flex-1 text-xs bg-primary-50 text-primary-700 px-2 py-1.5 rounded hover:bg-primary-100 border border-primary-200"
                    >
                      Reschedule
                    </button>
                    <button
                      onClick={() => openModal('cancel', booking)}
                      className="flex-1 text-xs bg-red-50 text-red-700 px-2 py-1.5 rounded hover:bg-red-100 border border-red-200"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Links */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-900">Booking Links</h3>
          <button
            onClick={() => setShowLinkForm(!showLinkForm)}
            className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700"
          >
            {showLinkForm ? 'Cancel' : '+ New Link'}
          </button>
        </div>

        {showLinkForm && (
          <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Assign to Agent (Optional)
                </label>
                <select
                  value={linkFormData.agentId}
                  onChange={(e) => setLinkFormData({ ...linkFormData, agentId: e.target.value })}
                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Customer Chooses</option>
                  {agents.map((agent) => (
                    <option key={agent.agentId} value={agent.agentId}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {linkFormData.agentId && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="lockAgentEmbed"
                    checked={linkFormData.lockAgent}
                    onChange={(e) => setLinkFormData({ ...linkFormData, lockAgent: e.target.checked })}
                    className="h-3 w-3 text-primary-600 border-gray-300 rounded"
                  />
                  <label htmlFor="lockAgentEmbed" className="ml-2 text-xs text-gray-700">
                    Force this agent (customer cannot choose different agent)
                  </label>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Meeting Type
                </label>
                <select
                  value={linkFormData.meetingType}
                  onChange={(e) => setLinkFormData({ ...linkFormData, meetingType: e.target.value })}
                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                >
                  <option value="phone">Phone Call</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="on-site">On-Site Support</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Duration
                </label>
                <select
                  value={linkFormData.duration}
                  onChange={(e) => setLinkFormData({ ...linkFormData, duration: e.target.value })}
                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>

              <button
                onClick={handleGenerateLink}
                disabled={generatingLink}
                className="w-full bg-primary-600 text-white text-xs px-3 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {generatingLink ? 'Generating...' : 'Generate Link'}
              </button>
            </div>
          </div>
        )}

        {data.links && data.links.length > 0 ? (
          <div className="space-y-2">
            {data.links.map((link) => (
              <div
                key={link.linkId}
                className="bg-white border border-gray-200 rounded p-3 text-xs"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 flex items-center gap-2">
                    {link.lockedAgentId ? (
                      <span className="inline-flex items-center px-2 py-1 rounded bg-primary-100 text-primary-800 text-xs font-medium">
                        Agent Locked
                      </span>
                    ) : (
                      <span className="text-gray-600">Any agent</span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      link.meetingType === 'teams' ? 'bg-blue-100 text-blue-800' : link.meetingType === 'on-site' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {link.meetingType === 'teams' ? 'Teams' : link.meetingType === 'on-site' ? 'On-Site' : 'Phone'}
                    </span>
                  </div>
                  {link.isUsed ? (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                      Used
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                      Active
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 rounded px-2 py-1 mb-2 break-all font-mono text-xs text-gray-700">
                  {`${window.location.origin}/book/${link.linkId}`}
                </div>

                <button
                  onClick={() => copyLink(`${window.location.origin}/book/${link.linkId}`)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded"
                >
                  Copy Link
                </button>
              </div>
            ))}
          </div>
        ) : (
          !showLinkForm && (
            <div className="text-center py-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded">
              No booking links created yet
            </div>
          )
        )}
      </div>

      {data.bookings.length === 0 && data.links.length === 0 && (
        <div className="text-center py-6 bg-primary-50 border border-primary-200 rounded">
          <p className="text-sm text-primary-800 mb-2">No scheduled appointments for this ticket</p>
          <p className="text-xs text-primary-600">Create a booking link to send to the customer</p>
        </div>
      )}

      </>}

      {/* Confirmation Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {modalState.type === 'reschedule' ? 'Reschedule Appointment' : 'Cancel Appointment'}
            </h3>

            <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
              <div><strong>Customer:</strong> {modalState.booking?.customerName}</div>
              <div><strong>Date:</strong> {modalState.booking && format(parseISO(modalState.booking.startTime), 'MMM d, yyyy \'at\' h:mm a')}</div>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              {modalState.type === 'reschedule'
                ? 'The customer will receive an email with a new link to reschedule their appointment.'
                : 'The customer will receive an email notifying them of the cancellation.'}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason {modalState.type === 'cancel' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                value={modalState.reason}
                onChange={(e) => setModalState(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={modalState.type === 'reschedule' ? 'e.g., Calendar conflict' : 'e.g., Customer requested cancellation'}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                rows={2}
              />
              {modalState.type === 'cancel' && (
                <p className="text-xs text-gray-500 mt-1">This reason will be shown to the customer.</p>
              )}
            </div>

            {modalState.error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {modalState.error}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={closeModal}
                disabled={modalState.processing}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-200 disabled:opacity-50"
              >
                Go Back
              </button>
              <button
                onClick={handleModalSubmit}
                disabled={modalState.processing}
                className={`flex-1 px-4 py-2 rounded text-sm text-white disabled:opacity-50 ${
                  modalState.type === 'cancel'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {modalState.processing
                  ? 'Processing...'
                  : modalState.type === 'reschedule'
                    ? 'Reschedule & Notify Customer'
                    : 'Cancel & Notify Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HaloIntegrationPage;
